import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { createContext, useContext, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { useApolloClient, useMutation } from "@apollo/client";
import { useAwaitQuery } from "@magento/peregrine/lib/hooks/useAwaitQuery";
import actions from "../store/actions/cart/actions";
import * as asyncActions from "../store/actions/cart/asyncActions";
import bindActionCreators from "@magento/peregrine/lib/util/bindActionCreators";
export const CartContext = createContext();
const isCartEmpty = cart => !cart || !cart.details || !cart.details.items || cart.details.items.length === 0;
const getTotalQuantity = items => items.filter(item => !!item).reduce((total, item) => total + item.quantity, 0);
const CartContextProvider = props => {
  const {
    actions,
    asyncActions,
    cartState,
    children
  } = props;
  // Make deeply nested details easier to retrieve and provide empty defaults
  const derivedDetails = useMemo(() => {
    if (isCartEmpty(cartState)) {
      return {
        currencyCode: "EUR",
        numItems: 0,
        subtotal: 0
      };
    } else {
      var _cartState$details, _cartState$details$pr, _cartState$details$pr2;
      return {
        currencyCode: cartState.details.prices.grand_total.currency,
        numItems: getTotalQuantity(cartState.details.items),
        subtotal: (_cartState$details = cartState.details) === null || _cartState$details === void 0 ? void 0 : (_cartState$details$pr = _cartState$details.prices) === null || _cartState$details$pr === void 0 ? void 0 : (_cartState$details$pr2 = _cartState$details$pr.subtotal_including_tax) === null || _cartState$details$pr2 === void 0 ? void 0 : _cartState$details$pr2.value
      };
    }
  }, [cartState]);
  const derivedCartState = _objectSpread(_objectSpread({}, cartState), {}, {
    isEmpty: isCartEmpty(cartState),
    derivedDetails
  });
  const cartApi = useMemo(() => _objectSpread({
    actions
  }, asyncActions), [actions, asyncActions]);
  const contextValue = useMemo(() => [derivedCartState, cartApi], [cartApi, derivedCartState]);
  const apolloClient = useApolloClient();
  const [fetchCartId] = useMutation(CREATE_CART_MUTATION);
  const fetchCartDetails = useAwaitQuery(CART_DETAILS_QUERY);
  useEffect(() => {
    // cartApi.getCartDetails initializes the cart if there isn't one. Also, we pass
    // apolloClient to wipe the store in event of auth token expiry which
    // will only happen if the user refreshes.
    cartApi.getCartDetails({
      apolloClient,
      fetchCartId,
      fetchCartDetails
    });
  }, [apolloClient, cartApi, fetchCartDetails, fetchCartId]);
  return /*#__PURE__*/React.createElement(CartContext.Provider, {
    value: contextValue
  }, children);
};
const mapStateToProps = _ref => {
  let {
    cart
  } = _ref;
  return {
    cartState: cart
  };
};
const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(actions, dispatch),
  asyncActions: bindActionCreators(asyncActions, dispatch)
});
export default connect(mapStateToProps, mapDispatchToProps)(CartContextProvider);
export const useCartContext = () => useContext(CartContext);

/**
 * We normally do not keep GQL queries in Peregrine. All components should pass
 * queries to talons/hooks. This is an exception to the rule because it would
 * be unecessarily complex to pass these queries to the context provider.
 */
const CREATE_CART_MUTATION = {
  "kind": "Document",
  "definitions": [{
    "kind": "OperationDefinition",
    "operation": "mutation",
    "name": {
      "kind": "Name",
      "value": "createCart"
    },
    "variableDefinitions": [],
    "directives": [],
    "selectionSet": {
      "kind": "SelectionSet",
      "selections": [{
        "kind": "Field",
        "alias": {
          "kind": "Name",
          "value": "cartId"
        },
        "name": {
          "kind": "Name",
          "value": "createEmptyCart"
        },
        "arguments": [],
        "directives": []
      }]
    }
  }],
  "loc": {
    "start": 0,
    "end": 65,
    "source": {
      "body": "\n    mutation createCart {\n        cartId: createEmptyCart\n    }\n",
      "name": "GraphQL request",
      "locationOffset": {
        "line": 1,
        "column": 1
      }
    }
  }
};
const CART_DETAILS_QUERY = {
  "kind": "Document",
  "definitions": [{
    "kind": "OperationDefinition",
    "operation": "query",
    "name": {
      "kind": "Name",
      "value": "checkUserIsAuthed"
    },
    "variableDefinitions": [{
      "kind": "VariableDefinition",
      "variable": {
        "kind": "Variable",
        "name": {
          "kind": "Name",
          "value": "cartId"
        }
      },
      "type": {
        "kind": "NonNullType",
        "type": {
          "kind": "NamedType",
          "name": {
            "kind": "Name",
            "value": "String"
          }
        }
      },
      "directives": []
    }],
    "directives": [],
    "selectionSet": {
      "kind": "SelectionSet",
      "selections": [{
        "kind": "Field",
        "name": {
          "kind": "Name",
          "value": "cart"
        },
        "arguments": [{
          "kind": "Argument",
          "name": {
            "kind": "Name",
            "value": "cart_id"
          },
          "value": {
            "kind": "Variable",
            "name": {
              "kind": "Name",
              "value": "cartId"
            }
          }
        }],
        "directives": [],
        "selectionSet": {
          "kind": "SelectionSet",
          "selections": [{
            "kind": "Field",
            "name": {
              "kind": "Name",
              "value": "id"
            },
            "arguments": [],
            "directives": []
          }]
        }
      }]
    }
  }],
  "loc": {
    "start": 0,
    "end": 271,
    "source": {
      "body": "\n    query checkUserIsAuthed($cartId: String!) {\n        cart(cart_id: $cartId) {\n            # The purpose of this query is to check that the user is authorized\n            # to query on the current cart. Just fetch \"id\" to keep it small.\n            id\n        }\n    }\n",
      "name": "GraphQL request",
      "locationOffset": {
        "line": 1,
        "column": 1
      }
    }
  }
};